import "@tento-ui/components/input/date-picker-modal";
import "@tento-ui/components/table/table";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Table } from "@tento-ui/components/table/table";
import { $$ } from "@tento-ui/fastdom";
import { type TasksClient } from "@tento-lona";
import { Network } from "@tento-lona/network";
import { TaskId, Actor } from "@tento-lona/sheets";
import { TaskRowItem } from "@tento-lona/sheets-ui/platform";
import { ArrowsAngleExpandIcon } from "@tento-ui/ui/icons";
import { TaskTableColumns } from "./task-table-columns";
import { Uuid } from "@tento-core/uuid";

export type NameMenuId = "edit" | "delete";
export type StatusMenuId = Network.TaskStatus;

@component({
  name: "lona-task-table",
})
export class TaskTable extends LonaWebComponent {
  $root: Table<TaskRowItem> = this.hydrate("root");
  private $nullState: HTMLElement = this.$("null-state");

  private tasks: Option<TasksClient.Api> = null;
  /** Self-echo actor for this table's task writes. */
  private readonly actor = new Actor("task-table");
  private expanded: boolean = false;
  private tvm: Option<Table.ViewModel<TaskRowItem>>;
  private pendingTasks: TaskRowItem[] = [];
  private cachedItems: TaskRowItem[] = [];

  private getStyle(expanded: boolean): Table.StyleOpt {
    if (expanded) {
      return {
        cellPadding: 4,
        rowHeight: 40,
        showRowDividers: false,
        layout: "default",
        containerMarginInline: 8,
      };
    } else {
      return {
        cellPadding: 4,
        rowHeight: 28,
        showRowDividers: false,
        layout: "inline",
        containerMarginInline: 0,
      };
    }
  }

  async bind(
    tasks: TasksClient.Api,
    options?: TaskTable.Options,
  ): Promise<void> {
    this.tasks = tasks;
    const { onClick, expanded = false, onSidebarFocusChanged } = options ?? {};
    this.expanded = expanded;

    const actions: TaskTableColumns.Actions = {
      tasks,
      actor: this.actor,
      removePending: (id) => {
        this.pendingTasks = this.pendingTasks.filter((t) => t.id !== id);
      },
      onChanged: () => this.rebindItems(),
    };

    this.tvm = Table.ViewModel.makeDefault({
      columns: TaskTableColumns.all(actions, onSidebarFocusChanged),
      style: this.getStyle(expanded),
      delegate: onClick
        ? {
            onRowClick: onClick,
            onContextMenu: onClick,
          }
        : undefined,
      floatingCta: onClick
        ? { icon: ArrowsAngleExpandIcon, onClick }
        : undefined,
      groups: expanded ? this.getGroups() : undefined,
    });

    this.$root.bind(this.tvm);
    this.cachedItems = (await tasks.list()).map(TaskRowItem.fromSnapshot);
    const items = this.getFilteredItems(expanded);
    this.$root.bindItems(items);
    this.$nullState.toggleAttribute("hidden", items.length > 0);
  }

  private getGroups(): Table.Group<TaskRowItem>[] {
    return [
      {
        id: "active",
        filter: (t) => t.status !== "completed",
        showRowDividers: true,
      },
      {
        id: "completed",
        filter: (t) => t.status === "completed",
        showRowDividers: true,
      },
    ];
  }

  private getFilteredItems(expanded: boolean): TaskRowItem[] {
    const items = [...this.pendingTasks, ...this.cachedItems];
    if (expanded) {
      return items;
    }
    return items.filter((t) => t.status !== "completed");
  }

  rebindItems(): void {
    if (!this.tasks) return;
    this.tasks.list().then((items) => {
      this.cachedItems = items.map(TaskRowItem.fromSnapshot);
      const filtered = this.getFilteredItems(this.expanded);
      this.$root.bindItems(filtered);
      this.$nullState.toggleAttribute("hidden", filtered.length > 0);
    });
  }

  insertPendingTask(): TaskId {
    if (!this.tasks) throw new Error("TaskTable not bound");

    const pendingId = TaskId.of(Uuid.make());
    const pendingTask = TaskRowItem.of({
      id: pendingId,
      title: "",
      isPending: true,
      completedAt: null,
      createdAt: null,
      description: null,
      displayId: null,
      displayNumber: null,
      dueDate: null,
      eventId: null,
      importance: null,
      position: null,
      scheduledEnd: null,
      scheduledStart: null,
      status: null,
    });

    this.pendingTasks.push(pendingTask);
    const items = this.getFilteredItems(this.expanded);
    this.$root.bindItems(items);
    this.$nullState.toggleAttribute("hidden", items.length > 0);

    $$.mutate(() => {
      this.startEditingTask(pendingId);
    });

    return pendingId;
  }

  private startEditingTask(taskId: TaskId) {
    // Find the row for this task and trigger editing on the name cell
    const $row = this.$root.findRowById(taskId);
    if (!$row) return;

    const $nameCell = $row.querySelector(".attr-name") as HTMLElement;
    if (!$nameCell) return;

    const $editableText = $nameCell.querySelector(
      "std-editable-text",
    ) as HTMLElement & {
      enableEditing?: (focus: boolean, end: boolean) => void;
    };
    if (!$editableText?.enableEditing) return;

    $editableText.enableEditing(true, true);
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
      }

      #root {
        width: 100%;
        height: 100%;
        color: var(--text-sec);
      }

      #null-state {
        margin-top: 0px;
        margin-bottom: 8px;
        margin-left: 12px;
        color: var(--text-ter);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-table id="root"></std-table>
    <p id="null-state">No tasks yet. Click + to add one.</p>
  `;
}

export namespace TaskTable {
  export type Options = {
    onClick?: Option<(t: TaskRowItem) => void>;
    expanded?: boolean;
    onSidebarFocusChanged?: (contextMenuId: string, focused: boolean) => void;
  };
}
